import { useState, useEffect } from 'react';
import { apiUrl, authFetch, authService, questionBankApi } from '../utils/api';
import { useUser } from '../contexts/UserContext';

interface QuestionOption {
  optionText: string;
  isCorrect: boolean;
  orderIndex: number;
}

interface QuestionAnswer {
  answerText: string;
  answerFormat: 'Text';
}

interface Teacher {
  id: string;
  fullName: string;
  email: string;
  phoneNumber: string;
  biography: string;
  qualifications: string;
  department: string;
  yearsOfExperience: number;
  imagePath: string | null;
  active: boolean;
  hireDate: string;
  createdDate: string;
  modificationDate: string;
  subjectCount: number;
  courseCount: number;
}

interface Subject {
  id: string;
  name: string;
  description: string;
  code: string;
  active: boolean;
  createdDate: string;
  modificationDate: string;
  gradeId: string;
  gradeName: string;
  teacherCount: number;
  courseCount: number;
}

interface Tag {
  tagId: string;
  teacherId: string;
  teacherName: string;
  subjectId: string;
  subjectName: string;
  term: string;
  name: string;
  description: string | null;
  createdAt: string;
}

type Term = 'First' | 'Second';

interface QuestionFormData {
  teacherId: string;
  subjectId: string;
  tagId: string;
  text: string;
  questionType: 'MULTIPLE_CHOICE' | 'TRUE_FALSE' | 'OPEN';
  options: {
    optionText: string;
    isCorrect: boolean;
    orderIndex: number;
  }[];
  answers?: {
    answerText: string;
    answerFormat: 'Text';
  }[];
}

interface BulkQuestionFormData {
  teacherId: string;
  subjectId: string;
  tagId: string;
  questions: QuestionFormData[];
}

interface QuestionOption {
  optionId: string;
  optionText: string;
  isCorrect: boolean;
  orderIndex: number;
}

interface QuestionTag {
  tagId: string;
  name: string;
  description: string | null;
  createdAt: string;
}

interface QuestionAnswer {
  answerId: string;
  answerText: string;
  answerFormat: 'Text';
}

interface Question {
  questionId: string;
  teacherId: string;
  teacherName: string;
  subjectId: string;
  subjectName: string;
  curriculumId: string | null;
  curriculumName: string | null;
  curriculumUnitId: string | null;
  curriculumUnitName: string | null;
  curriculumLessonId: string | null;
  curriculumLessonTitle: string | null;
  courseId: string | null;
  courseName: string | null;
  unitId: string | null;
  unitName: string | null;
  lessonId: string | null;
  lessonName: string | null;
  questionType: string;
  text: string;
  difficulty: string;
  createdAt: string;
  updatedAt: string;
  options: QuestionOption[];
  answers: QuestionAnswer[];
  tags: QuestionTag[];
  tag?: {
    tagId: string;
    teacherId: string;
    teacherName: string;
    subjectId: string;
    subjectName: string;
    term: string;
    name: string;
    description: string | null;
    createdAt: string;
  };
}

interface Grade {
  id: string;
  name: string;
  description: string;
  level: number;
  code: string;
  active: boolean;
}

interface QuestionFilters {
  teacherId?: string;
  subjectId?: string;
  questionType?: string;
  searchText?: string;
  gradeId?: string;
  page: number;
  pageSize: number;
}

const QuestionBank = () => { 
  const { user, isTeacher, isModerator, getTeacherId } = useUser();
  
  // State
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<QuestionFilters>({
    page: 1,
    pageSize: 10,
    // Auto-filter by teacherId for Teacher role
    ...(isTeacher() && getTeacherId() && { teacherId: getTeacherId()! })
  });
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState<Question | null>(null);
  const [bulkFormData, setBulkFormData] = useState<BulkQuestionFormData>({
    teacherId: '',
    subjectId: '',
    tagId: '',
    questions: []
  });
  const [currentQuestion, setCurrentQuestion] = useState<QuestionFormData>({
    teacherId: '',
    subjectId: '',
    tagId: '',
    text: '',
    questionType: 'MULTIPLE_CHOICE',
    options: [{ optionText: '', isCorrect: false, orderIndex: 0 }]
  });
  const [saving, setSaving] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState<Question | null>(null);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [loadingTeachers, setLoadingTeachers] = useState(false);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [loadingTags, setLoadingTags] = useState(false);
  const [tags, setTags] = useState<Tag[]>([]);
  const [grades, setGrades] = useState<Grade[]>([]);
  const [loadingGrades, setLoadingGrades] = useState(false);
  const [showAddTagModal, setShowAddTagModal] = useState(false);
  const [selectedTerm, setSelectedTerm] = useState<Term | ''>('');
  const [newTagName, setNewTagName] = useState('');

  // Handle delete question
  const handleDeleteQuestion = async (questionId: string) => {
    try {
      const response = await authFetch(apiUrl(`QuestionBank/${questionId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });

      if (response.ok) {
        fetchQuestions(); // Refresh the list
        setShowDeleteConfirm(false);
        setQuestionToDelete(null);
        alert('تم حذف السؤال بنجاح');
      } else {
        const data = await response.json();
        throw new Error(data.message || 'Failed to delete question');
      }
    } catch (error) {
      console.error('Error deleting question:', error);
      alert('Failed to delete question. Please try again later.');
    }
  };

  // Fetch teachers
  const fetchTeachers = async () => {
    setLoadingTeachers(true);
    try {
      const response = await authFetch(apiUrl('Teacher'), {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setTeachers(data.data);
      } else {
        throw new Error(data.message || 'Failed to fetch teachers');
      }
    } catch (error) {
      console.error('Error fetching teachers:', error);
      setTeachers([]);
    } finally {
      setLoadingTeachers(false);
    }
  };

  // Fetch grades
  const fetchGrades = async () => {
    setLoadingGrades(true);
    try {
      const response = await authFetch(apiUrl('Grade'), {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setGrades(data.data);
      }
    } catch (error) {
      console.error('Error fetching grades:', error);
      setGrades([]);
    } finally {
      setLoadingGrades(false);
    }
  };

  // Fetch tags based on selected teacher, subject and term
  const fetchTags = async () => {
    if (!currentQuestion.teacherId || !currentQuestion.subjectId || !selectedTerm) {
      setTags([]);
      return;
    }

    setLoadingTags(true);
    try {
      const queryParams = new URLSearchParams({
        TeacherId: currentQuestion.teacherId,
        SubjectId: currentQuestion.subjectId,
        Term: selectedTerm
      });

      const response = await authFetch(apiUrl(`Tag?${queryParams.toString()}`), {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setTags(data.data.tags);
      } else {
        throw new Error(data.message || 'Failed to fetch tags');
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
      setTags([]);
    } finally {
      setLoadingTags(false);
    }
  };

  // Handle adding new tag
  const handleAddTag = async () => {
    if (!currentQuestion.teacherId || !currentQuestion.subjectId || !selectedTerm || !newTagName.trim()) {
      return;
    }

    try {
      const response = await authFetch(apiUrl('Tag'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify({
          teacherId: currentQuestion.teacherId,
          subjectId: currentQuestion.subjectId,
          term: selectedTerm,
          name: newTagName.trim()
        })
      });

      const data = await response.json();

      if (data.success) {
        setShowAddTagModal(false);
        setNewTagName('');
        fetchTags(); // Refresh tags list
        alert('تم إضافة الدرس بنجاح');
      } else {
        throw new Error(data.message || 'Failed to add tag');
      }
    } catch (error) {
      console.error('Error adding tag:', error);
      alert('Failed to add tag. Please try again later.');
    }
  };

  // Fetch subjects for selected teacher
  const fetchSubjects = async (teacherId: string) => {
    if (!teacherId) {
      setSubjects([]);
      return;
    }

    setLoadingSubjects(true);
    try {
      const response = await authFetch(apiUrl(`Teacher/${teacherId}/subjects`), {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setSubjects(data.data);
      } else {
        throw new Error(data.message || 'Failed to fetch subjects');
      }
    } catch (error) {
      console.error('Error fetching subjects:', error);
      setSubjects([]);
    } finally {
      setLoadingSubjects(false);
    }
  };

  // Fetch questions
  const fetchQuestions = async () => {
    setLoading(true);
    setError(null);

    try {
      const filtersToSend = { ...filters };
      
      // Convert filter keys to match API expectations
      const apiFilters: any = {
        Page: filtersToSend.page,
        PageSize: filtersToSend.pageSize
      };
      
      if (filtersToSend.teacherId) apiFilters.TeacherId = filtersToSend.teacherId;
      if (filtersToSend.subjectId) apiFilters.SubjectId = filtersToSend.subjectId;
      if (filtersToSend.questionType) apiFilters.QuestionType = filtersToSend.questionType;
      if (filtersToSend.searchText) apiFilters.SearchText = filtersToSend.searchText;
      if (filtersToSend.gradeId) apiFilters.GradeId = filtersToSend.gradeId;

      const data = await questionBankApi.getQuestions(apiFilters);

      if (data.success) {
        setQuestions(data.data.questions);
        setTotalItems(data.data.totalCount);
        setTotalPages(Math.ceil(data.data.totalCount / filtersToSend.pageSize));
      } else {
        throw new Error(data.message || 'Failed to fetch questions');
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
      setError('Failed to load questions. Please try again later.');
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle filter changes
  const handleFilterChange = (name: keyof QuestionFilters, value: string): void => {
    setFilters(prev => ({
      ...prev,
      [name]: value,
      page: 1 // Reset to first page when filters change
    }));
  };

  // Handle page change
  const handlePageChange = (pageNumber: number) => {
    setFilters(prev => ({
      ...prev,
      page: pageNumber
    }));
  };

  // Handle teacher selection change
  const handleTeacherChange = (teacherId: string) => {
    setCurrentQuestion(prev => ({
      ...prev,
      teacherId,
      subjectId: '', // Reset subject when teacher changes
      tagId: '' // Reset tag when teacher changes
    }));
    setBulkFormData(prev => ({
      ...prev,
      teacherId,
      subjectId: '',
      tagId: '',
      questions: [] // Reset questions when teacher changes
    }));
    fetchSubjects(teacherId);
  };

  // Auto-select teacher for Teacher role users
  useEffect(() => {
    if (isTeacher() && user && !currentQuestion.teacherId) {
      const teacherId = user.id;
      setCurrentQuestion(prev => ({ ...prev, teacherId }));
      setBulkFormData(prev => ({ ...prev, teacherId }));
      fetchSubjects(teacherId);
    }
  }, [user, isTeacher, currentQuestion.teacherId]);

  // Reset filters
  const handleResetFilters = () => {
    setFilters({
      page: 1,
      pageSize: 10,
      // Preserve teacherId filter for Teacher role users
      ...(isTeacher() && getTeacherId() && { teacherId: getTeacherId()! })
    });
  };

  // Handle adding current question to the bulk list
  const handleAddQuestion = () => {
    // Validate current question
    if (!currentQuestion.teacherId) {
      alert('يرجى اختيار المدرس أولاً');
      return;
    }

    if (!currentQuestion.subjectId) {
      alert('يرجى اختيار المادة أولاً');
      return;
    }

    if (!currentQuestion.tagId) {
      alert('يرجى اختيار الدرس أولاً');
      return;
    }

    if (!currentQuestion.text) {
      alert('يرجى إدخال نص السؤال');
      return;
    }

    if (currentQuestion.questionType === 'MULTIPLE_CHOICE' && !currentQuestion.options.some(opt => opt.isCorrect)) {
      alert('يرجى تحديد الإجابة الصحيحة للسؤال');
      return;
    }

    if (currentQuestion.questionType === 'OPEN' && (!currentQuestion.answers || !currentQuestion.answers[0]?.answerText)) {
      alert('يرجى إدخال الإجابة النموذجية للسؤال');
      return;
    }

    // Add the current question to the bulk list
    setBulkFormData(prev => ({
      ...prev,
      teacherId: currentQuestion.teacherId,
      subjectId: currentQuestion.subjectId,
      tagId: currentQuestion.tagId,
      questions: [...prev.questions, { ...currentQuestion }]
    }));

    // Reset current question form but keep the selected teacher, subject, and tag
    setCurrentQuestion(prev => {
      const base = {
        teacherId: prev.teacherId,
        subjectId: prev.subjectId,
        tagId: prev.tagId,
        text: '',
        questionType: prev.questionType,
        options: prev.questionType === 'OPEN' ? [] :
                 prev.questionType === 'MULTIPLE_CHOICE' ? [{ optionText: '', isCorrect: false, orderIndex: 0 }] :
                 prev.questionType === 'TRUE_FALSE' ? [
                   { optionText: 'True', isCorrect: true, orderIndex: 0 },
                   { optionText: 'False', isCorrect: false, orderIndex: 1 }
                 ] : [],
      };
      
      // Only include answers array for OPEN questions
      if (prev.questionType === 'OPEN') {
        return { ...base, answers: [] };
      }
      return base;
    });
  };

  // Handle removing a question from the bulk list
  const handleRemoveQuestion = (index: number) => {
    setBulkFormData(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index)
    }));
  };

  // Handle form submission
  const handleSubmit = async () => {

    setSaving(true);

    try {
      const questionsData = {
        questions: bulkFormData.questions.map(question => {
          if (question.questionType === 'OPEN') {
            // For OPEN questions, include answers but omit options
            const { options, ...questionWithoutOptions } = question;
            return questionWithoutOptions;
          } else {
            // For TRUE_FALSE and MULTIPLE_CHOICE, include options but omit answers
            const { answers, ...questionWithoutAnswers } = question;
            return questionWithoutAnswers;
          }
        })
      };

      const data = await questionBankApi.addQuestions(questionsData);

      if (data.success) {
        setShowAddModal(false);
        setBulkFormData({
          teacherId: '',
          subjectId: '',
          tagId: '',
          questions: []
        });
        setCurrentQuestion({
          teacherId: '',
          subjectId: '',
          tagId: '',
          text: '',
          questionType: 'MULTIPLE_CHOICE',
          options: [{ optionText: '', isCorrect: false, orderIndex: 0 }],
          answers: [{ answerText: '', answerFormat: 'Text' }]
        });
        setSelectedTerm('');
        setSubjects([]);
        setTags([]);
        fetchQuestions(); // Refresh the list
        alert('تم إضافة الأسئلة بنجاح');
      } else {
        throw new Error(data.message || 'Failed to add questions');
      }
    } catch (error) {
      console.error('Error adding questions:', error);
      alert('Failed to add questions. Please try again later.');
    } finally {
      setSaving(false);
    }
  };

  // Handle option changes for multiple choice questions
  const handleOptionChange = (index: number, field: keyof QuestionOption, value: string | boolean) => {
    setCurrentQuestion(prev => ({
      ...prev,
      options: prev.options.map((option, i) =>
        i === index ? { ...option, [field]: value } : option
      )
    }));
  };

  // Add new option for multiple choice questions
  const handleAddOption = () => {
    setCurrentQuestion(prev => ({
      ...prev,
      options: [...prev.options, { optionText: '', isCorrect: false, orderIndex: prev.options.length }]
    }));
  };

  // Remove option from multiple choice questions
  const handleRemoveOption = (index: number) => {
    setCurrentQuestion(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  // Fetch question details
  const fetchQuestionDetails = async (questionId: string) => {
    setLoadingDetails(true);
    try {
      const response = await authFetch(apiUrl(`QuestionBank/${questionId}`), {
        headers: {
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });

      const data = await response.json();

      if (data.success) {
        const questionDetails = data.data;
        
        // Initialize options based on question type
        let initialOptions = questionDetails.options || [];
        if (questionDetails.questionType === 'TRUE_FALSE' && (!initialOptions || initialOptions.length === 0)) {
          initialOptions = [
            { optionId: 'true', optionText: 'صح', isCorrect: true, orderIndex: 0 },
            { optionId: 'false', optionText: 'خطأ', isCorrect: false, orderIndex: 1 }
          ];
        }

        setEditFormData({
          ...questionDetails,
          options: initialOptions,
          answers: questionDetails.answers || [],
          tags: questionDetails.tags || []
        });
        setSelectedQuestion(questionDetails);
      } else {
        throw new Error(data.message || 'Failed to fetch question details');
      }
    } catch (error) {
      console.error('Error fetching question details:', error);
      alert('Failed to load question details. Please try again later.');
    } finally {
      setLoadingDetails(false);
    }
  };

  // Handle question click
  const handleQuestionClick = async (question: Question) => {
    setShowDetailsModal(true);
    setIsEditing(false);
    await fetchQuestionDetails(question.questionId);
  };

  // Handle save question
  const handleSaveQuestion = async () => {
    if (!editFormData) return;

    try {
      const response = await authFetch(apiUrl(`QuestionBank/${editFormData.questionId}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify({
          questionId: editFormData.questionId,
          tagId: editFormData.tag?.tagId,
          text: editFormData.text,
          questionType: editFormData.questionType,
          ...(editFormData.questionType === 'OPEN' 
            ? {
                answers: editFormData.answers.map(ans => ({
                  answerId: ans.answerId,
                  answerText: ans.answerText,
                  answerFormat: ans.answerFormat
                }))
              }
            : {
                options: editFormData.options.map(opt => ({
                  optionId: opt.optionId,
                  optionText: opt.optionText,
                  isCorrect: opt.isCorrect,
                  orderIndex: opt.orderIndex
                }))
              }
          )
        })
      });

      const data = await response.json();

      if (data.success) {
        setSelectedQuestion(editFormData);
        setIsEditing(false);
        fetchQuestions(); // Refresh the list
        alert('تم حفظ التغييرات بنجاح');
      } else {
        throw new Error(data.message || 'Failed to save question');
      }
    } catch (error) {
      console.error('Error saving question:', error);
      alert('Failed to save changes. Please try again later.');
    }
  };

  // Fetch questions when filters change
  useEffect(() => {
    fetchQuestions();
  }, [filters]);

  // Fetch teachers and grades when component mounts
  useEffect(() => {
    fetchTeachers();
    fetchGrades();
  }, []);

  // Update filters with teacherId when user becomes available (for Teacher role)
  useEffect(() => {
    if (isTeacher()) {
      const teacherId = getTeacherId();
      if (teacherId) {
        setFilters(prev => {
          // Only update if teacherId is not already set or is different
          if (prev.teacherId !== teacherId) {
            return {
              ...prev,
              teacherId: teacherId
            };
          }
          return prev;
        });
      }
    }
  }, [user, isTeacher, getTeacherId]);

  // Fetch tags when term changes
  useEffect(() => {
    if (selectedTerm && currentQuestion.teacherId && currentQuestion.subjectId) {
      fetchTags();
    }
  }, [selectedTerm, currentQuestion.teacherId, currentQuestion.subjectId]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100" dir="rtl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                بنك الأسئلة
              </h1>
              <p className="mt-2 text-gray-600">إدارة أسئلة المواد الدراسية</p>
            </div>
            <button
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm rounded-lg 
                       shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 focus:outline-none focus:ring-2 
                       focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              إضافة سؤال جديد
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 transition-all duration-200 hover:shadow-xl">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-1">
              <label htmlFor="search-text" className="block text-sm font-medium text-gray-700">
                بحث
              </label>
              <input
                type="text"
                id="search-text"
                value={filters.searchText || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFilterChange('searchText', e.target.value)}
                className="block w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="البحث في الأسئلة..."
              />
            </div>

            <div className="space-y-1">
              <label htmlFor="question-type" className="block text-sm font-medium text-gray-700">
                نوع السؤال
              </label>
              <select
                id="question-type"
                value={filters.questionType || ''}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleFilterChange('questionType', e.target.value)}
                className="block w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">جميع الأنواع</option>
                <option value="MULTIPLE_CHOICE">اختيار من متعدد</option>
                <option value="TRUE_FALSE">صح وخطأ</option>
                <option value="OPEN">مقالي</option>
              </select>
            </div>

            <div className="space-y-1">
              <label htmlFor="grade-filter" className="block text-sm font-medium text-gray-700">
                الصف الدراسي
              </label>
              <select
                id="grade-filter"
                value={filters.gradeId || ''}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleFilterChange('gradeId', e.target.value)}
                className="block w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={loadingGrades}
              >
                <option value="">
                  {loadingGrades ? 'جاري تحميل الصفوف...' : 'جميع الصفوف'}
                </option>
                {grades.map((grade) => (
                  <option key={grade.id} value={grade.id}>
                    {grade.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-4">
            <button
              onClick={() => fetchQuestions()}
              className="inline-flex items-center px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm rounded-lg
                       shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 focus:outline-none focus:ring-2
                       focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              تطبيق التصفية
            </button>
            <button
              onClick={handleResetFilters}
              className="inline-flex items-center px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 text-sm transition-colors duration-200"
            >
              إعادة تعيين
            </button>
          </div>
        </div>

        {/* Questions Table */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-200 hover:shadow-xl">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr className="bg-gradient-to-r from-gray-50 to-gray-100">
                  <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    نص السؤال
                  </th>
                  <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    نوع السؤال
                  </th>
                  <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    اسم المعلم
                  </th>
                  <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    المادة
                  </th>
                  <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    إجراءات
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      </div>
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-4">
                      <div className="flex items-center justify-center text-red-500">
                        <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {error}
                      </div>
                    </td>
                  </tr>
                ) : questions.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center text-gray-500">
                        <svg className="w-12 h-12 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <p className="text-lg font-medium">لم يتم العثور على أسئلة</p>
                        <p className="text-sm">حاول تعديل التصفية للعثور على ما تبحث عنه</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  questions.map((question) => (
                    <tr 
                      key={question.questionId} 
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleQuestionClick(question)}
                    >
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{question.text}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-500">
                          {question.questionType === 'MULTIPLE_CHOICE' ? 'اختيار من متعدد' :
                           question.questionType === 'TRUE_FALSE' ? 'صح وخطأ' :
                           question.questionType === 'OPEN' ? 'مقالي' : question.questionType}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{question.teacherName}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{question.subjectName}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex justify-end">
                          <button
                            onClick={(e: { stopPropagation: () => void }) => {
                              e.stopPropagation(); // Prevent row click
                              setQuestionToDelete(question);
                              setShowDeleteConfirm(true);
                            }}
                            className="text-red-600 hover:text-red-800 transition-colors duration-200"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && questionToDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md border border-gray-200 transform transition-all">
              <div className="text-center mb-6">
                <div className="w-12 h-12 rounded-full bg-red-100 mx-auto mb-4 flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h3 className="text-xl font-medium text-gray-900 mb-2">تأكيد الحذف</h3>
                <p className="text-gray-500">
                  هل أنت متأكد من حذف هذا السؤال؟ لا يمكن التراجع عن هذا الإجراء.
                </p>
              </div>
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setQuestionToDelete(null);
                  }}
                  className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-all focus:outline-none focus:ring-2 focus:ring-gray-300"
                >
                  إلغاء
                </button>
                <button
                  onClick={() => handleDeleteQuestion(questionToDelete.questionId)}
                  className="px-4 py-2 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 transition-all focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                >
                  حذف
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Pagination */}
        {!loading && questions.length > 0 && totalPages > 1 && (
          <div className="mt-6 flex justify-center">
            <nav className="flex items-center space-x-reverse space-x-2">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`px-3 py-1 rounded-lg text-sm font-medium ${
                    filters.page === page
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-500 hover:bg-gray-100'
                  }`}
                >
                  {page}
                </button>
              ))}
            </nav>
          </div>
        )}

        {/* Add Question Modal */}
        {showAddModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl border border-gray-200 transform transition-all flex flex-col max-h-[90vh]">
              <div className="p-6 border-b">
                <div className="text-center">
                  <h3 className="text-xl font-medium text-gray-900">إضافة سؤال جديد</h3>
                </div>
              </div>

              <form onSubmit={(e: React.FormEvent) => {
                e.preventDefault();
                // If there's a current question with data, ask if they want to add it
                if (currentQuestion.text) {
                  if (window.confirm('هناك سؤال لم تتم إضافته للقائمة. هل تريد إضافته قبل الحفظ؟')) {
                    handleAddQuestion();
                  }
                }
                
                if (bulkFormData.questions.length === 0) {
                  alert('يرجى إضافة سؤال واحد على الأقل');
                  return;
                }

                handleSubmit();
              }} className="flex flex-col flex-1 overflow-hidden" noValidate>
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  {/* Teacher Selection - Only for Moderators */}
                  {isModerator() && (
                    <div>
                      <label htmlFor="teacherId" className="block text-sm font-medium text-gray-700 mb-1">
                        اختر المدرس
                      </label>
                      <select
                        id="teacherId"
                        value={currentQuestion.teacherId}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleTeacherChange(e.target.value)}
                        className="block w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        aria-required="true"
                        disabled={loadingTeachers}
                      >
                        <option value="">اختر المدرس</option>
                        {teachers.map((teacher) => (
                          <option key={teacher.id} value={teacher.id}>
                            {teacher.fullName}
                          </option>
                        ))}
                      </select>
                      {loadingTeachers && (
                        <div className="mt-1 text-sm text-gray-500">جاري تحميل المدرسين...</div>
                      )}
                    </div>
                  )}

                  {/* Teacher Info Display - Only for Teachers */}
                  {isTeacher() && user && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">المدرس</label>
                      <div className="block w-full px-4 py-2 rounded-lg border border-gray-200 bg-gray-50 text-gray-700">
                        {user.name}
                      </div>
                    </div>
                  )}

                  <div>
                    <label htmlFor="subjectId" className="block text-sm font-medium text-gray-700 mb-1">
                      اختر المادة
                    </label>
                    <select
                      id="subjectId"
                      value={currentQuestion.subjectId}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                        setCurrentQuestion(prev => ({ ...prev, subjectId: e.target.value }));
                        setBulkFormData(prev => ({ ...prev, subjectId: e.target.value }));
                      }}
                      className="block w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      aria-required="true"
                      disabled={!currentQuestion.teacherId || loadingSubjects}
                    >
                      <option value="">اختر المادة</option>
                      {subjects.map((subject) => (
                        <option key={subject.id} value={subject.id}>
                          {subject.name}
                        </option>
                      ))}
                    </select>
                    {loadingSubjects && (
                      <div className="mt-1 text-sm text-gray-500">جاري تحميل المواد...</div>
                    )}
                    {!currentQuestion.teacherId && (
                      <div className="mt-1 text-sm text-gray-500">يرجى اختيار المدرس أولاً</div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="term" className="block text-sm font-medium text-gray-700 mb-1">
                      الفصل الدراسي
                    </label>
                    <select
                      id="term"
                      value={selectedTerm}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                        setSelectedTerm(e.target.value as Term);
                        setTags([]); // Reset tags when term changes
                      }}
                      className="block w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      aria-required="true"
                      disabled={!currentQuestion.teacherId || !currentQuestion.subjectId}
                    >
                      <option value="">اختر الفصل الدراسي</option>
                      <option value="First">الفصل الدراسي الأول</option>
                      <option value="Second">الفصل الدراسي الثاني</option>
                    </select>
                    {(!currentQuestion.teacherId || !currentQuestion.subjectId) && (
                      <div className="mt-1 text-sm text-gray-500">يرجى اختيار المدرس والمادة أولاً</div>
                    )}
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <label htmlFor="tagId" className="block text-sm font-medium text-gray-700">
                        اختر الدرس
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowAddTagModal(true)}
                        className="text-sm text-blue-600 hover:text-blue-800"
                        disabled={!currentQuestion.teacherId || !currentQuestion.subjectId || !selectedTerm}
                      >
                        + إضافة درس جديد
                      </button>
                    </div>
                    <select
                      id="tagId"
                      value={currentQuestion.tagId}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                        setCurrentQuestion(prev => ({ ...prev, tagId: e.target.value }));
                        setBulkFormData(prev => ({ ...prev, tagId: e.target.value }));
                      }}
                      className="block w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      aria-required="true"
                      disabled={!selectedTerm || loadingTags}
                    >
                      <option value="">اختر الدرس</option>
                      {tags.map((tag) => (
                        <option key={tag.tagId} value={tag.tagId}>
                          {tag.name}
                        </option>
                      ))}
                    </select>
                    {loadingTags && (
                      <div className="mt-1 text-sm text-gray-500">جاري تحميل الدروس...</div>
                    )}
                    {!selectedTerm && (
                      <div className="mt-1 text-sm text-gray-500">يرجى اختيار الفصل الدراسي أولاً</div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="questionType" className="block text-sm font-medium text-gray-700 mb-1">
                      نوع السؤال
                    </label>
                    <select
                      id="questionType"
                      value={currentQuestion.questionType}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setCurrentQuestion(prev => ({ ...prev, questionType: e.target.value as 'MULTIPLE_CHOICE' | 'TRUE_FALSE' | 'OPEN' }))}
                      className="block w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      aria-required="true"
                    >
                      <option value="MULTIPLE_CHOICE">اختيار من متعدد</option>
                      <option value="TRUE_FALSE">صح وخطأ</option>
                      <option value="OPEN">مقالي</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="text" className="block text-sm font-medium text-gray-700 mb-1">
                    نص السؤال
                  </label>
                  <textarea
                    id="text"
                    value={currentQuestion.text}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setCurrentQuestion(prev => ({ ...prev, text: e.target.value }))}
                    rows={3}
                    className="block w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                {currentQuestion.questionType === 'MULTIPLE_CHOICE' && (
                  <div className="space-y-4">
                    <label className="block text-sm font-medium text-gray-700">الخيارات</label>
                    {currentQuestion.options.map((option, index) => (
                      <div key={index} className="flex items-center gap-4">
                        <input
                          type="text"
                          value={option.optionText}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleOptionChange(index, 'optionText', e.target.value)}
                          className="flex-1 px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder={`الخيار ${index + 1}`}
                          aria-required="true"
                        />
                        <label className="inline-flex items-center">
                          <input
                            type="radio"
                            name="correctOption"
                            checked={option.isCorrect}
                            onChange={() => {
                              setCurrentQuestion(prev => ({
                                ...prev,
                                options: prev.options.map((opt, i) => ({
                                  ...opt,
                                  isCorrect: i === index
                                }))
                              }));
                            }}
                            className="ml-2"
                          />
                          <span className="text-sm text-gray-700">إجابة صحيحة</span>
                        </label>
                        {index > 0 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveOption(index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={handleAddOption}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      + إضافة خيار
                    </button>
                  </div>
                )}

                {currentQuestion.questionType === 'TRUE_FALSE' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">الإجابة الصحيحة</label>
                    <div className="flex items-center gap-4">
                      <label className="inline-flex items-center">
                        <input
                          type="radio"
                          name="trueFalseAnswer"
                          checked={currentQuestion.options[0]?.isCorrect}
                          onChange={() => {
                            setCurrentQuestion(prev => ({
                              ...prev,
                              options: [
                                { optionText: 'True', isCorrect: true, orderIndex: 0 },
                                { optionText: 'False', isCorrect: false, orderIndex: 1 }
                              ]
                            }));
                          }}
                          className="ml-2"
                        />
                        <span className="text-sm text-gray-700">صح</span>
                      </label>
                      <label className="inline-flex items-center">
                        <input
                          type="radio"
                          name="trueFalseAnswer"
                          checked={currentQuestion.options[1]?.isCorrect}
                          onChange={() => {
                            setCurrentQuestion(prev => ({
                              ...prev,
                              options: [
                                { optionText: 'True', isCorrect: false, orderIndex: 0 },
                                { optionText: 'False', isCorrect: true, orderIndex: 1 }
                              ]
                            }));
                          }}
                          className="ml-2"
                        />
                        <span className="text-sm text-gray-700">خطأ</span>
                      </label>
                    </div>
                  </div>
                )}

                {currentQuestion.questionType === 'OPEN' && (
                  <div>
                    <label htmlFor="answer" className="block text-sm font-medium text-gray-700 mb-1">
                      الإجابة النموذجية
                    </label>
                    <div className="space-y-4">
                      {(currentQuestion.answers || []).map((answer, index) => (
                        <div key={index} className="flex items-center gap-4">
                          <textarea
                            value={answer.answerText}
                            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                              const newAnswers = [...(currentQuestion.answers || [])];
                              newAnswers[index] = { answerText: e.target.value, answerFormat: 'Text' };
                              setCurrentQuestion(prev => ({
                                ...prev,
                                answers: newAnswers
                              }));
                            }}
                            rows={2}
                            className="flex-1 px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            aria-required="true"
                            placeholder={`الإجابة النموذجية ${index + 1}`}
                          />
                          {index > 0 && (
                            <button
                              type="button"
                              onClick={() => {
                                const newAnswers = (currentQuestion.answers || []).filter((_, i) => i !== index);
                                setCurrentQuestion(prev => ({
                                  ...prev,
                                  answers: newAnswers
                                }));
                              }}
                              className="text-red-600 hover:text-red-800"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => {
                          setCurrentQuestion(prev => ({
                            ...prev,
                            answers: [...(prev.answers || []), { answerText: '', answerFormat: 'Text' }]
                          }));
                        }}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        + إضافة إجابة نموذجية
                      </button>
                    </div>
                  </div>
                )}

                {/* Added Questions List */}
                {bulkFormData.questions.length > 0 && (
                  <div className="mt-8 border-t pt-6">
                    <h4 className="text-lg font-medium text-gray-900 mb-4">الأسئلة المضافة ({bulkFormData.questions.length})</h4>
                    <div className="space-y-4">
                      {bulkFormData.questions.map((question, index) => (
                        <div key={index} className="bg-gray-50 rounded-lg p-4 relative">
                          <button
                            onClick={() => handleRemoveQuestion(index)}
                            className="absolute top-2 left-2 text-red-600 hover:text-red-800"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                          <div className="text-sm">
                            <div className="font-medium text-gray-900 mb-1">السؤال {index + 1}</div>
                            <div className="text-gray-600">{question.text}</div>
                            <div className="mt-2 text-gray-500">
                              {question.questionType === 'MULTIPLE_CHOICE' ? 'اختيار من متعدد' :
                               question.questionType === 'TRUE_FALSE' ? 'صح وخطأ' :
                               question.questionType === 'OPEN' ? 'مقالي' : question.questionType}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                </div>
                <div className="p-6 border-t bg-gray-50">
                  <div className="flex justify-between gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddModal(false);
                        setSubjects([]); // Reset subjects when modal is closed
                        setBulkFormData({
                          teacherId: '',
                          subjectId: '',
                          tagId: '',
                          questions: []
                        });
                      }}
                      className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-all focus:outline-none focus:ring-2 focus:ring-gray-300"
                    >
                      إلغاء
                    </button>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleAddQuestion}
                        disabled={!currentQuestion.text || saving}
                        className="px-4 py-2 rounded-lg bg-green-600 text-white font-medium hover:bg-green-700 transition-all focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50"
                      >
                        إضافة للقائمة
                      </button>
                      <button
                        type="submit"
                        disabled={saving || (bulkFormData.questions.length === 0 && !currentQuestion.text)}
                        className="px-4 py-2 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 flex items-center gap-2"
                      >
                        {saving ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            جاري الحفظ...
                          </>
                        ) : (
                          'حفظ الأسئلة'
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Add Tag Modal */}
        {showAddTagModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md border border-gray-200 transform transition-all">
              <div className="text-center mb-5">
                <h3 className="text-xl font-medium text-gray-900 mb-2">إضافة درس جديد</h3>
              </div>

              <div className="space-y-4">
                {/* Teacher Selection - Only for Moderators */}
                {isModerator() && (
                  <div>
                    <label htmlFor="teacherIdTag" className="block text-sm font-medium text-gray-700 mb-1">
                      المدرس
                    </label>
                    <select
                      id="teacherIdTag"
                      value={currentQuestion.teacherId}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleTeacherChange(e.target.value)}
                      className="block w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                      disabled={loadingTeachers}
                    >
                      <option value="">اختر المدرس</option>
                      {teachers.map((teacher) => (
                        <option key={teacher.id} value={teacher.id}>
                          {teacher.fullName}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Teacher Info Display - Only for Teachers */}
                {isTeacher() && user && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">المدرس</label>
                    <div className="block w-full px-4 py-2 rounded-lg border border-gray-200 bg-gray-50 text-gray-700">
                      {user.name}
                    </div>
                  </div>
                )}

                <div>
                  <label htmlFor="subjectIdTag" className="block text-sm font-medium text-gray-700 mb-1">
                    المادة
                  </label>
                  <select
                    id="subjectIdTag"
                    value={currentQuestion.subjectId}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                      setCurrentQuestion(prev => ({ ...prev, subjectId: e.target.value }));
                      setBulkFormData(prev => ({ ...prev, subjectId: e.target.value }));
                    }}
                    className="block w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                    disabled={!currentQuestion.teacherId || loadingSubjects}
                  >
                    <option value="">اختر المادة</option>
                    {subjects.map((subject) => (
                      <option key={subject.id} value={subject.id}>
                        {subject.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="termTag" className="block text-sm font-medium text-gray-700 mb-1">
                    الفصل الدراسي
                  </label>
                  <select
                    id="termTag"
                    value={selectedTerm}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedTerm(e.target.value as Term)}
                    className="block w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">اختر الفصل الدراسي</option>
                    <option value="First">الفصل الدراسي الأول</option>
                    <option value="Second">الفصل الدراسي الثاني</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="tagName" className="block text-sm font-medium text-gray-700 mb-1">
                    اسم الدرس
                  </label>
                  <input
                    type="text"
                    id="tagName"
                    value={newTagName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewTagName(e.target.value)}
                    className="block w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                    placeholder="أدخل اسم الدرس"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddTagModal(false);
                    setNewTagName('');
                  }}
                  className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-all focus:outline-none focus:ring-2 focus:ring-gray-300"
                >
                  إلغاء
                </button>
                <button
                  type="button"
                  onClick={handleAddTag}
                  disabled={!currentQuestion.teacherId || !currentQuestion.subjectId || !selectedTerm || !newTagName.trim()}
                  className="px-4 py-2 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                >
                  إضافة الدرس
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Question Details Modal */}
        {showDetailsModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl border border-gray-200 transform transition-all flex flex-col max-h-[90vh]">
              <div className="p-6 border-b">
                <h3 className="text-xl font-medium text-gray-900">تفاصيل السؤال</h3>
              </div>
              <div className="flex-1 overflow-y-auto p-6">
                <div className="space-y-4">
                  {loadingDetails ? (
                    <div className="flex justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  ) : selectedQuestion && editFormData ? (
                    <div className="space-y-4">
                  {/* Metadata Section */}
                  <div className="bg-gray-50 rounded-lg p-4 mb-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-500">المعلم</label>
                        <p className="mt-1 text-base text-gray-900">{editFormData.teacherName}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500">المادة</label>
                        <p className="mt-1 text-base text-gray-900">{editFormData.subjectName}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500">الفصل الدراسي</label>
                        <p className="mt-1 text-base text-gray-900">
                          {editFormData.tag?.term === 'First' ? 'الفصل الدراسي الأول' :
                           editFormData.tag?.term === 'Second' ? 'الفصل الدراسي الثاني' : ''}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500">الدرس</label>
                        <p className="mt-1 text-base text-gray-900">{editFormData.tag?.name}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500">نوع السؤال</label>
                        <p className="mt-1 text-base text-gray-900">
                          {editFormData.questionType === 'MULTIPLE_CHOICE' ? 'اختيار من متعدد' :
                           editFormData.questionType === 'TRUE_FALSE' ? 'صح وخطأ' :
                           editFormData.questionType === 'OPEN' ? 'مقالي' : editFormData.questionType}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Question Text Section */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-500 mb-2">نص السؤال</label>
                    {isEditing ? (
                      <textarea
                        value={editFormData.text}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEditFormData({ ...editFormData, text: e.target.value })}
                        className="block w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        rows={3}
                      />
                    ) : (
                      <div className="p-4 bg-white rounded-lg border border-gray-200">
                        <p className="text-base text-gray-900">{editFormData.text}</p>
                      </div>
                    )}
                  </div>

                  {/* Options Section */}
                  {(editFormData.questionType === 'MULTIPLE_CHOICE' || editFormData.questionType === 'TRUE_FALSE') && editFormData.options && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-2">الخيارات</label>
                      <div className="space-y-2">
                        {editFormData.options.map((option, index) => (
                          <div key={option.optionId} className="flex items-center gap-4">
                            {isEditing && editFormData.questionType === 'MULTIPLE_CHOICE' ? (
                              <>
                                <input
                                  type="text"
                                  value={option.optionText}
                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    const newOptions = [...editFormData.options];
                                    newOptions[index] = { ...option, optionText: e.target.value };
                                    setEditFormData({ ...editFormData, options: newOptions });
                                  }}
                                  className="flex-1 px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                                <label className="inline-flex items-center">
                                  <input
                                    type="radio"
                                    checked={option.isCorrect}
                                    onChange={() => {
                                      const newOptions = editFormData.options.map((opt, i) => ({
                                        ...opt,
                                        isCorrect: i === index
                                      }));
                                      setEditFormData({ ...editFormData, options: newOptions });
                                    }}
                                    className="ml-2"
                                  />
                                  <span className="text-sm text-gray-700">إجابة صحيحة</span>
                                </label>
                              </>
                            ) : editFormData.questionType === 'TRUE_FALSE' ? (
                              <label className="inline-flex items-center flex-1">
                                <input
                                  type="radio"
                                  checked={option.isCorrect}
                                  onChange={() => {
                                    if (isEditing) {
                                      const newOptions = editFormData.options.map((opt, i) => ({
                                        ...opt,
                                        isCorrect: i === index
                                      }));
                                      setEditFormData({ ...editFormData, options: newOptions });
                                    }
                                  }}
                                  disabled={!isEditing}
                                  className="ml-2"
                                />
                                <span className={`text-sm ${option.isCorrect ? 'text-green-700 font-medium' : 'text-gray-700'}`}>
                                  {option.optionText}
                                </span>
                              </label>
                            ) : (
                              <div className={`flex-1 p-2 rounded ${option.isCorrect ? 'bg-green-50 text-green-700' : ''}`}>
                                {option.optionText}
                                {option.isCorrect && ' (إجابة صحيحة)'}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Essay Answer Section */}
                  {editFormData.questionType === 'OPEN' && editFormData.answers && editFormData.answers.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500">الإجابة النموذجية</label>
                      {isEditing ? (
                        <textarea
                          value={editFormData.answers[0].answerText}
                          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                            const newAnswers = [...editFormData.answers];
                            newAnswers[0] = { ...newAnswers[0], answerText: e.target.value };
                            setEditFormData({ ...editFormData, answers: newAnswers });
                          }}
                          className="mt-1 block w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          rows={3}
                        />
                      ) : (
                        <p className="mt-1 text-base text-gray-900">{editFormData.answers[0].answerText}</p>
                      )}
                    </div>
                  )}

                   {/* Tags Section */}
                   {editFormData.tags && editFormData.tags.length > 0 && (
                     <div>
                       <label className="block text-sm font-medium text-gray-500 mb-2">الوسوم</label>
                       <div className="flex flex-wrap gap-2">
                         {editFormData.tags.map((tag) => (
                           <span key={tag.tagId} className="px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-sm">
                             {tag.name}
                           </span>
                         ))}
                       </div>
                     </div>
                   )}
                    </div>
                  ) : null}
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  {loadingDetails ? (
                    <button
                      onClick={() => setShowDetailsModal(false)}
                      className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-all focus:outline-none focus:ring-2 focus:ring-gray-300"
                    >
                      إغلاق
                    </button>
                  ) : isEditing ? (
                    <>
                      <button
                        onClick={() => {
                          setEditFormData(selectedQuestion);
                          setIsEditing(false);
                        }}
                        className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-all focus:outline-none focus:ring-2 focus:ring-gray-300"
                      >
                        إلغاء
                      </button>
                      <button
                        onClick={handleSaveQuestion}
                        className="px-4 py-2 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                      >
                        حفظ التغييرات
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => setShowDetailsModal(false)}
                        className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-all focus:outline-none focus:ring-2 focus:ring-gray-300"
                      >
                        إغلاق
                      </button>
                      <button
                        onClick={() => setIsEditing(true)}
                        className="px-4 py-2 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                      >
                        تعديل
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default QuestionBank;
